"use client"

import { useState, useEffect, useCallback } from "react"
import { useAuth } from "../../context/AuthContext"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Search,
  Plus,
  Minus,
  ShoppingBag,
  AlertTriangle,
  X,
  Check,
  Coffee,
  ChevronRight,
  Clock,
  DollarSign,
  BookOpen,
  Filter,
  Menu,
  Info,
  Package,
  Trash2,
} from "lucide-react"
import { API_URL } from "../../api/config"

const MenuSelection = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { tableId, existingHoldBillId, existingItems, isAddingItems } = location.state || {}
  const [selectedItems, setSelectedItems] = useState([])
  const { userToken: token } = useAuth()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [showVariantModal, setShowVariantModal] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [tableHoldBills, setTableHoldBills] = useState([])
  const [showHoldBillsModal, setShowHoldBillsModal] = useState(false)
  const [selectedHoldBill, setSelectedHoldBill] = useState(null)
  const [customAlertVisible, setCustomAlertVisible] = useState(false)
  const [customAlertOptions, setCustomAlertOptions] = useState({
    title: "",
    message: "",
    buttons: [],
  })

  const [toasts, setToasts] = useState([])
  const [activeTab, setActiveTab] = useState("all")
  const [isHoldBillSectionExpanded, setIsHoldBillSectionExpanded] = useState(true)

  const addToast = (message, type = "info") => {
    const id = Date.now()
    setToasts((prev) => [...prev, { id, message, type }])
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id))
    }, 3000)
  }

  useEffect(() => {
    if (isAddingItems && existingItems) {
      // Transform existing items to match the expected format
      const transformedItems = existingItems.map((item) => ({
        _id: item.productId || item._id,
        itemName: item.itemName,
        quantity: item.quantity || 1,
        price: item.price,
        pricingType: item.pricingType,
        category: item.category,
        selectedVariant: item.variant,
        names: item.names || "",
        // Keep original fields for reference
        originalItem: item,
      }))
      setSelectedItems(transformedItems)
    } else if (tableId && !isAddingItems && !existingHoldBillId) {
      fetchTableHoldBills()
    }
  }, [isAddingItems, existingItems, tableId, existingHoldBillId])

  useEffect(() => {
    const handleBackButton = (e) => {
      e.preventDefault()
      showBackConfirmation()
    }
    window.addEventListener("popstate", handleBackButton)
    return () => window.removeEventListener("popstate", handleBackButton)
  }, [selectedItems, isAddingItems, existingHoldBillId])

  const showBackConfirmation = () => {
    const isCreatingNewHoldBill = !isAddingItems && !existingHoldBillId
    const isUpdatingHoldBill = isAddingItems && existingHoldBillId
    const hasSelectedItems = selectedItems.length > 0

    setCustomAlertVisible(true)
    setCustomAlertOptions({
      title: "Confirm Action",
      message: hasSelectedItems
        ? "You have selected items. What would you like to do?"
        : "Are you sure you want to go back?",
      buttons: [
        ...(!isCreatingNewHoldBill && hasSelectedItems
          ? [
              {
                text: isAddingItems ? "Update Hold Bill" : "Update",
                onPress: handleProceed,
                style: "primary",
              },
            ]
          : []),
        ...(!isUpdatingHoldBill && hasSelectedItems
          ? [
              {
                text: "Hold Bill",
                onPress: handleDirectHold,
                style: "danger",
              },
            ]
          : []),
        {
          text: "Continue Editing",
          style: "default",
        },
        {
          text: "Cancel",
          onPress: () => navigate(-1),
          style: "cancel",
        },
      ],
    })
  }

  const CustomAlertModal = () => (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 transition-opacity duration-300 ${customAlertVisible ? "opacity-100" : "opacity-0 pointer-events-none"}`}
    >
      <div className="bg-white rounded-lg w-11/12 max-w-md overflow-hidden shadow-xl transform transition-all">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-800 px-6 py-4">
          <h3 className="text-xl font-bold text-white">{customAlertOptions.title}</h3>
        </div>
        <div className="p-6">
          <p className="text-gray-700 mb-4">{customAlertOptions.message}</p>
          <div className="flex flex-col gap-2">
            {customAlertOptions.buttons.map((button, index) => (
              <button
                key={index}
                className={`py-3 px-4 rounded-lg font-semibold text-center transition-colors duration-200 
                  ${
                    button.style === "primary"
                      ? "bg-blue-600 text-white hover:bg-blue-700"
                      : button.style === "danger"
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : button.style === "cancel"
                          ? "bg-gray-200 text-gray-800 hover:bg-gray-300"
                          : "bg-gray-100 text-gray-800 hover:bg-gray-200"
                  }`}
                onClick={() => {
                  setCustomAlertVisible(false)
                  button.onPress && button.onPress()
                }}
              >
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )

  const fetchTableHoldBills = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/hold-bills/table/${tableId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch hold bills for the table")
      }
      const data = await response.json()
      if (data.length > 0) {
        setTableHoldBills(data)
        setShowHoldBillsModal(true)
      }
    } catch (error) {
      console.error("Error fetching table hold bills:", error)
      addToast("Error: Failed to load existing hold bills", "error")
    } finally {
      setLoading(false)
    }
  }

  const selectExistingHoldBill = (holdBill) => {
    setSelectedHoldBill(holdBill)
    const transformedItems = holdBill.items.map((item) => ({
      _id: item.productId,
      itemName: item.itemName,
      quantity: item.quantity,
      price: item.price,
      pricingType: item.pricingType,
      category: item.category,
      selectedVariant: item.variant,
      names: item.names,
      originalItem: item,
    }))
    setSelectedItems(transformedItems)
    setShowHoldBillsModal(false)
    navigate("/menu-selection", {
      state: {
        tableId,
        existingHoldBillId: holdBill._id,
        existingItems: transformedItems,
        isAddingItems: true,
      },
    })
  }

  const fetchProducts = useCallback(async () => {
    setLoading(true)
    try {
      if (!token) {
        throw new Error("Authentication token is missing")
      }
      const response = await fetch(`${API_URL}/products`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
      if (!response.ok) {
        throw new Error("Failed to fetch products")
      }
      const data = await response.json()
      const inStockProducts = data.filter((product) => product.inStock)
      setProducts(inStockProducts)
    } catch (error) {
      addToast("Error: Failed to load menu items", "error")
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.type.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesCategory = categoryFilter === "all" || product.category === categoryFilter
    const matchesType = activeTab === "all" || product.type === activeTab
    return matchesSearch && matchesCategory && matchesType
  })

  const groupedProducts = filteredProducts.reduce((acc, product) => {
    if (!acc[product.type]) {
      acc[product.type] = []
    }
    acc[product.type].push(product)
    return acc
  }, {})

  const handleQuantityChange = (product, change) => {
    setSelectedItems((prevItems) => {
      const existingItemIndex = prevItems.findIndex((item) => {
        if (product.selectedVariant) {
          return item._id === product._id && item.selectedVariant?.name === product.selectedVariant.name
        }
        return item._id === product._id && !item.selectedVariant
      })

      if (existingItemIndex === -1 && change > 0) {
        // Add new item
        const newItem = {
          ...product,
          quantity: 1,
          price: product.selectedVariant
            ? product.selectedVariant.price
            : product.pricingType === "mrp"
              ? product.sellingPrice || product.mrp
              : product.basePrice,
        }
        return [...prevItems, newItem]
      } else if (existingItemIndex !== -1) {
        // Update existing item
        const updatedItems = [...prevItems]
        const currentItem = updatedItems[existingItemIndex]
        const newQuantity = (currentItem.quantity || 1) + change

        if (newQuantity <= 0) {
          // Remove item if quantity becomes 0 or negative
          updatedItems.splice(existingItemIndex, 1)
        } else {
          // Update quantity
          updatedItems[existingItemIndex] = { ...currentItem, quantity: newQuantity }
        }
        return updatedItems
      }
      return prevItems
    })
  }

  const getItemQuantity = (product) => {
    if (product.variants && product.variants.length > 0) {
      const variantItems = selectedItems.filter((item) => item._id === product._id && item.selectedVariant)
      return variantItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
    }

    const item = selectedItems.find((item) => item._id === product._id && !item.selectedVariant)
    return item ? item.quantity || 1 : 0
  }

  const totalQuantity = selectedItems.reduce((sum, item) => sum + (item.quantity || 1), 0)
  const isSelected = totalQuantity > 0
  const totalAmount = selectedItems.reduce((sum, item) => {
    const price = item.selectedVariant ? item.selectedVariant.price : item.price
    return sum + price * (item.quantity || 1)
  }, 0)

  const handleItemPress = (product) => {
    if (product.variants && product.variants.length > 0) {
      setSelectedProduct(product)
      setShowVariantModal(true)
    } else {
      const baseProduct = {
        ...product,
        price: product.pricingType === "mrp" ? product.sellingPrice || product.mrp : product.basePrice,
      }
      handleQuantityChange(baseProduct, 1)
    }
  }

  // FIXED handleProceed function
  const handleProceed = async () => {
    if (selectedItems.length === 0) {
      addToast("Error: Please select at least one item", "error")
      return
    }

    if (isAddingItems && existingHoldBillId) {
      setLoading(true)
      try {
        // Format items with consistent structure
        const formattedItems = selectedItems.map((item) => {
          const productId = item._id || item.productId

          return {
            productId: productId,
            _id: productId, // Include both for compatibility
            itemName: item.itemName,
            quantity: item.quantity || 1,
            category: item.category || "Veg",
            pricingType: item.pricingType,
            price: item.selectedVariant ? item.selectedVariant.price : item.price,
            selectedVariant: item.selectedVariant,
            variant: item.selectedVariant, // Include both for compatibility
            names: item.names || "",
          }
        })

        console.log("Sending update request with items:", formattedItems)

        const updateResponse = await fetch(`${API_URL}/hold-bills/${existingHoldBillId}`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            items: formattedItems,
            action: "replace", // Replace all items with the current selection
          }),
        })

        const responseData = await updateResponse.json()

        if (!updateResponse.ok) {
          if (responseData.invalidProducts && responseData.invalidProducts.length > 0) {
            const invalidItems = responseData.invalidProducts
              .map((p) => `${p.productName || p.productId}: ${p.message}`)
              .join("\n")

            throw new Error(`Invalid items found:\n${invalidItems}`)
          }
          throw new Error(responseData.message || "Failed to update hold bill")
        }

        addToast("Success: Hold bill updated successfully", "success")
        navigate("/new-bill")
      } catch (error) {
        console.error("Error updating hold bill:", error)
        addToast(`Update Failed: ${error.message}`, "error")
      } finally {
        setLoading(false)
      }
    } else {
      navigate("/bill-details", {
        state: { selectedItems, tableId, isHoldBill: true },
      })
    }
  }

  const handleAddDetails = () => {
    if (selectedItems.length === 0) {
      addToast("Error: Please select at least one item", "error")
      return
    }
    navigate("/hold-bill", { state: { selectedItems, tableId } })
  }

  const handleDirectHold = async () => {
    if (selectedItems.length === 0) {
      addToast("Error: Please select at least one item", "error")
      return
    }
    setLoading(true)
    try {
      const response = await fetch(`${API_URL}/hold-bills`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          tableId,
          items: selectedItems.map((item) => ({
            productId: item._id,
            quantity: item.quantity,
            variant: item.selectedVariant,
            names: item.names,
          })),
          status: "HOLD",
          timestamp: new Date().toISOString(),
        }),
      })
      if (!response.ok) throw new Error("Failed to hold bill")
      addToast("Success: Bill placed on hold successfully", "success")
      navigate("/new-bill")
    } catch (error) {
      addToast("Error: Failed to hold bill", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleUpdateExistingItem = (updatedItem) => {
    setSelectedItems((prevItems) => {
      if (updatedItem.quantity <= 0) {
        // Remove item if quantity is 0 or less
        return prevItems.filter((item) => {
          const isMatch =
            item._id === updatedItem._id &&
            (item.selectedVariant?.name === updatedItem.selectedVariant?.name ||
              (!item.selectedVariant && !updatedItem.selectedVariant))
          return !isMatch
        })
      }

      return prevItems.map((item) => {
        // Match by productId and variant
        const isMatch =
          item._id === updatedItem._id &&
          (item.selectedVariant?.name === updatedItem.selectedVariant?.name ||
            (!item.selectedVariant && !updatedItem.selectedVariant))

        if (isMatch) {
          return { ...item, quantity: updatedItem.quantity }
        }
        return item
      })
    })
  }

  const handleRemoveExistingItem = (itemToRemove) => {
    setSelectedItems((prevItems) => {
      return prevItems.filter((item) => {
        // Check if this is the item to remove by comparing both ID and variant
        const isSameProduct =
          item._id === itemToRemove._id || item.productId === itemToRemove._id || item._id === itemToRemove.productId

        // For variant products, we need to check if the variant matches
        if (itemToRemove.selectedVariant && item.selectedVariant) {
          return !(isSameProduct && item.selectedVariant.name === itemToRemove.selectedVariant.name)
        } else if (!itemToRemove.selectedVariant && !item.selectedVariant) {
          // For non-variant products, just check the product ID
          return !isSameProduct
        }
        // If one has variant and other doesn't, they're different items
        return true
      })
    })

    // Add a toast notification for better user feedback
    addToast(`Removed ${itemToRemove.itemName} from hold bill`, "success")
  }

  const MenuItem = ({ product }) => {
    const quantity = getItemQuantity(product)
    const existingItemWithVariant = selectedItems.find((item) => item._id === product._id && item.selectedVariant)
    const existingItemNoVariant = selectedItems.find((item) => item._id === product._id && !item.selectedVariant)
    const isInHoldBill = existingItems && existingItems.some((item) => item._id === product._id)
    const isExistingItem = isInHoldBill && !quantity

    const getPriceDisplay = () => {
      if (product.variants && product.variants.length > 0) {
        return (
          <div>
            <p className="text-base font-semibold text-indigo-800">
              From ₹{Math.min(...product.variants.map((v) => v.price))}
            </p>
            <p className="text-xs text-gray-500 mt-1 pt-1 border-t border-dashed border-gray-200">
              {product.variants.length} variants available
            </p>
          </div>
        )
      }
      if (product.pricingType === "mrp") {
        return (
          <div>
            <p className="text-base font-semibold text-indigo-800 flex items-center">
              <DollarSign size={16} className="mr-1" /> ₹{product.sellingPrice || product.mrp}
            </p>
          </div>
        )
      }
      return (
        <p className="text-base font-semibold text-indigo-800 flex items-center">
          <DollarSign size={16} className="mr-1" /> ₹{product.basePrice}
        </p>
      )
    }

    const handleDirectAdd = (e) => {
      e.stopPropagation()
      if (product.variants && product.variants.length > 0) {
        if (existingItemWithVariant) {
          handleQuantityChange(existingItemWithVariant, 1)
        } else {
          setSelectedProduct(product)
          setShowVariantModal(true)
        }
      } else {
        const baseProduct = {
          ...product,
          price: product.pricingType === "mrp" ? product.sellingPrice || product.mrp : product.basePrice,
        }
        handleQuantityChange(baseProduct, 1)
      }
    }

    const handleDirectRemove = (e) => {
      e.stopPropagation()
      if (quantity > 0) {
        if (!product.variants || product.variants.length === 0) {
          if (existingItemNoVariant) handleQuantityChange(existingItemNoVariant, -1)
        } else if (existingItemWithVariant) {
          handleQuantityChange(existingItemWithVariant, -1)
        }
      }
    }

    return (
      <div
        className={`bg-white rounded-xl shadow-md p-5 relative cursor-pointer transition-all duration-300 hover:shadow-lg border-2 
          ${quantity > 0 ? "border-blue-500 bg-blue-50" : "border-transparent hover:border-blue-300"}
          ${isExistingItem ? "border-l-4 border-red-500" : ""} transform hover:-translate-y-1`}
        onClick={() => handleItemPress(product)}
      >
        {isExistingItem && (
          <div className="absolute top-0 right-0 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs font-bold py-1 px-2 rounded-bl-lg shadow-md">
            In Hold Bill
          </div>
        )}
        <div className="absolute top-2 left-2">
          <div
            className={`w-3 h-3 rounded-full ${product.category === "Veg" ? "bg-green-500" : "bg-red-500"}`}
            aria-label={product.category}
          />
        </div>
        {product.imageUrl && (
          <img
            src={product.imageUrl || "/placeholder.svg"}
            alt={`${product.itemName} image`}
            className="w-full h-32 object-cover rounded-lg mb-3"
          />
        )}
        <div className="mb-3">
          <h3 className="text-lg font-bold text-gray-800 mb-2 pr-6">{product.itemName}</h3>
          {getPriceDisplay()}
          {existingItemWithVariant && (
            <p className="text-sm font-medium text-blue-600 mt-2">
              Selected: {existingItemWithVariant.selectedVariant.name}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between bg-gray-100 rounded-lg p-2">
          <button
            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 
              ${quantity === 0 ? "bg-gray-300 cursor-not-allowed" : "bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow hover:shadow-md"}`}
            onClick={handleDirectRemove}
            disabled={quantity === 0}
            aria-label="Decrease quantity"
          >
            <Minus size={18} />
          </button>
          <span className="text-lg font-bold text-gray-800">{quantity}</span>
          <button
            className="w-9 h-9 rounded-full flex items-center justify-center bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow transition-all hover:shadow-md"
            onClick={handleDirectAdd}
            aria-label="Increase quantity"
          >
            <Plus size={18} />
          </button>
        </div>
      </div>
    )
  }

  const VariantSelectionModal = () => (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 transition-opacity duration-300 ${showVariantModal ? "opacity-100" : "opacity-0 pointer-events-none"}`}
    >
      <div className="bg-white rounded-xl w-11/12 max-w-md overflow-hidden shadow-xl transform transition-all">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4">
          <h3 className="text-xl font-bold text-white flex items-center">
            <BookOpen className="mr-2" size={20} />
            {selectedProduct?.itemName} - Select Variant
          </h3>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto">
          {selectedProduct?.variants.map((variant) => (
            <div
              key={variant.name}
              className="flex justify-between items-center p-4 my-2 border border-gray-200 rounded-lg hover:bg-blue-50 transition-colors duration-200 cursor-pointer"
              onClick={() => {
                handleQuantityChange({ ...selectedProduct, selectedVariant: variant }, 1)
                setShowVariantModal(false)
              }}
            >
              <p className="text-gray-800 font-medium">{variant.name}</p>
              <p className="text-indigo-700 font-bold">₹{variant.price}</p>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-gray-200">
          <button
            className="w-full py-3 px-4 bg-pink-600 hover:bg-pink-700 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
            onClick={() => setShowVariantModal(false)}
          >
            <X className="mr-2" size={18} />
            Cancel
          </button>
        </div>
      </div>
    </div>
  )

  const HoldBillsSelectionModal = () => (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 transition-opacity duration-300 ${showHoldBillsModal ? "opacity-100" : "opacity-0 pointer-events-none"}`}
    >
      <div className="bg-white rounded-xl w-11/12 max-w-2xl overflow-hidden shadow-xl transform transition-all">
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 px-6 py-4">
          <h3 className="text-xl font-bold text-white flex items-center">
            <Clock className="mr-2" size={20} />
            Existing Hold Bills for Table {tableId}
          </h3>
        </div>
        <div className="p-4 max-h-96 overflow-y-auto">
          {tableHoldBills.map((holdBill) => (
            <div
              key={holdBill._id}
              className="bg-white border border-gray-200 rounded-lg p-4 mb-4 hover:border-amber-400 hover:shadow-md transition-all cursor-pointer"
              onClick={() => selectExistingHoldBill(holdBill)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-bold text-gray-800">Bill #{holdBill._id.substring(holdBill._id.length - 6)}</p>
                  <p className="text-sm text-gray-500 flex items-center mt-1">
                    <Clock size={14} className="mr-1" />
                    {new Date(holdBill.createdAt).toLocaleString()}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-700">{holdBill.items.length} items</p>
                  <p className="font-bold text-amber-600">₹{holdBill.totalAmount}</p>
                </div>
              </div>

              {holdBill.names && <p className="text-sm text-indigo-600 mt-2">Name: {holdBill.names}</p>}
              <div className="mt-3 border-t border-gray-100 pt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">Items:</p>
                <div className="space-y-2">
                  {holdBill.items.slice(0, 3).map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <div className="flex items-center">
                        <div
                          className={`w-2 h-2 rounded-full ${item.category === "Veg" ? "bg-green-500" : "bg-red-500"} mr-2`}
                        ></div>
                        <p className="text-sm">
                          {item.itemName}
                          {item.variant ? ` (${item.variant.name})` : ""}
                        </p>
                      </div>
                      <p className="text-sm font-medium">x{item.quantity}</p>
                    </div>
                  ))}
                  {holdBill.items.length > 3 && (
                    <p className="text-xs text-gray-500 italic">+{holdBill.items.length - 3} more items</p>
                  )}
                </div>
              </div>
              <div className="mt-3 flex justify-end">
                <span className="text-sm text-blue-600 font-medium flex items-center">
                  Update <ChevronRight size={16} />
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t border-gray-200 flex gap-3">
          <button
            className="flex-1 py-3 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold rounded-lg transition-colors duration-200"
            onClick={() => setShowHoldBillsModal(false)}
          >
            Cancel
          </button>
          <button
            className="flex-1 py-3 px-4 bg-amber-600 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
            onClick={() => {
              setShowHoldBillsModal(false)
              navigate("/menu-selection", { state: { tableId, isAddingItems: false } })
            }}
          >
            Create New
          </button>
        </div>
      </div>
    </div>
  )

  const Toast = ({ message, type }) => {
    const bgColor = type === "success" ? "bg-green-500" : type === "error" ? "bg-red-500" : "bg-blue-500"
    const Icon = type === "success" ? Check : type === "error" ? AlertTriangle : Info

    return (
      <div className={`${bgColor} text-white px-6 py-4 rounded-lg shadow-lg flex items-center mb-3 animate-fade-in`}>
        <Icon className="mr-3" size={20} />
        <p>{message}</p>
      </div>
    )
  }

  const categories = ["all", ...new Set(products.map((product) => product.category))]
  const productTypes = ["all", ...new Set(products.map((product) => product.type))]

  return (
    <div className="bg-gray-50 min-h-screen pb-24">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-800 text-white p-4 shadow-lg sticky top-0 z-10">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Menu className="mr-3" size={24} />
            <h1 className="text-xl font-bold">Menu Selection</h1>
          </div>
          <div className="flex space-x-2">
            {tableId && (
              <div className="bg-white bg-opacity-20 rounded-lg px-3 py-1 text-sm font-semibold">Table {tableId}</div>
            )}
            {isAddingItems && existingItems && (
              <div className="bg-amber-500 rounded-lg px-3 py-1 text-sm font-semibold">Updating Hold Bill</div>
            )}
          </div>
        </div>
      </div>

      <div className="p-4 bg-white shadow-md sticky top-16 z-10">
        <div className="flex items-center space-x-2 mb-4">
          <div className="relative flex-1">
            <input
              className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search for items..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search className="absolute left-3 top-3 text-gray-400" size={20} />
          </div>
          <button
            className="p-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            onClick={() => navigate("/filter")}
          >
            <Filter size={20} />
          </button>
        </div>
        <div className="overflow-x-auto pb-2">
          <div className="flex space-x-2 min-w-max">
            {productTypes.map((type) => (
              <button
                key={type}
                className={`py-2 px-4 rounded-full text-sm font-semibold transition-colors ${
                  activeTab === type
                    ? "bg-indigo-600 text-white shadow-md"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setActiveTab(type)}
              >
                {type === "all" ? "All Items" : type}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hold Bill Items Section - Always visible when items exist */}
      {isAddingItems && selectedItems && selectedItems.length > 0 && (
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-b-4 border-amber-400 shadow-lg">
          <div className="p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-amber-800 flex items-center">
                <Package className="mr-2" size={24} />
                Current Hold Bill Items
                <span className="ml-2 bg-amber-200 text-amber-800 text-sm px-3 py-1 rounded-full font-semibold">
                  {selectedItems.length} items
                </span>
              </h2>
              <div className="text-right">
                <p className="text-sm text-amber-600">Total Amount</p>
                <p className="text-2xl font-bold text-amber-800">
                  ₹
                  {selectedItems
                    .reduce((sum, item) => {
                      const price = item.selectedVariant ? item.selectedVariant.price : item.price
                      return sum + price * (item.quantity || 1)
                    }, 0)
                    .toFixed(2)}
                </p>
              </div>
            </div>

            {/* Items Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
              {selectedItems.map((item, index) => {
                const itemKey = `${item._id || item.productId}-${item.selectedVariant?.name || "base"}-${index}`

                return (
                  <div
                    key={itemKey}
                    className="bg-white rounded-lg shadow-md p-4 border-l-4 border-amber-400 hover:shadow-lg transition-all relative"
                  >
                    {/* Remove button */}
                    <button
                      className="absolute top-2 right-2 p-1.5 text-red-500 hover:text-white hover:bg-red-500 rounded-full transition-all z-10"
                      onClick={() => handleRemoveExistingItem(item)}
                      title="Remove item"
                    >
                      <Trash2 size={16} />
                    </button>

                    <div className="flex justify-between items-start mb-2 pr-8">
                      <h4 className="font-semibold text-gray-800 text-sm">{item.itemName}</h4>
                      <div
                        className={`w-3 h-3 rounded-full ${item.category === "Veg" ? "bg-green-500" : "bg-red-500"}`}
                      ></div>
                    </div>

                    {item.selectedVariant && (
                      <p className="text-xs text-indigo-600 font-medium mb-2">{item.selectedVariant.name}</p>
                    )}

                    <div className="flex justify-between items-center mt-2">
                      <p className="font-bold text-indigo-800 text-sm">
                        ₹{item.selectedVariant ? item.selectedVariant.price : item.price}
                      </p>

                      <div className="flex items-center bg-gray-100 rounded-lg overflow-hidden">
                        <button
                          className="p-1.5 text-white bg-red-500 hover:bg-red-600 transition"
                          onClick={() =>
                            handleUpdateExistingItem({ ...item, quantity: Math.max(0, (item.quantity || 1) - 1) })
                          }
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-2 font-semibold text-sm">{item.quantity || 1}</span>
                        <button
                          className="p-1.5 text-white bg-indigo-600 hover:bg-indigo-700 transition"
                          onClick={() => handleUpdateExistingItem({ ...item, quantity: (item.quantity || 1) + 1 })}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Item total */}
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-600 text-right">
                        Total:{" "}
                        <span className="font-bold text-indigo-800">
                          ₹
                          {(
                            (item.selectedVariant ? item.selectedVariant.price : item.price) * (item.quantity || 1)
                          ).toFixed(2)}
                        </span>
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-amber-200">
              <button
                className="flex-1 py-3 px-4 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
                onClick={handleProceed}
                disabled={loading}
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                ) : (
                  <Check className="mr-2" size={18} />
                )}
                Update Hold Bill
              </button>
              <button
                className="py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
                onClick={() => {
                  setCustomAlertVisible(true)
                  setCustomAlertOptions({
                    title: "Clear All Items",
                    message: "Are you sure you want to remove all items from this hold bill?",
                    buttons: [
                      {
                        text: "Cancel",
                        style: "default",
                      },
                      {
                        text: "Clear All",
                        onPress: () => setSelectedItems([]),
                        style: "danger",
                      },
                    ],
                  })
                }}
              >
                <X className="mr-2" size={18} />
                Clear All
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
          </div>
        ) : Object.entries(groupedProducts).length > 0 ? (
          Object.entries(groupedProducts).map(([type, typeProducts]) => (
            <div key={type} className="mb-6">
              <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <Coffee className="mr-2" size={20} />
                {type}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {typeProducts.map((product) => (
                  <MenuItem key={product._id} product={product} />
                ))}
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center h-40 bg-white rounded-lg shadow p-6">
            <AlertTriangle size={40} className="text-amber-500 mb-4" />
            <p className="text-gray-700 text-lg font-medium mb-2">No items found</p>
            <p className="text-gray-500 text-center">
              {searchQuery ? `No results for "${searchQuery}"` : "No menu items available"}
            </p>
          </div>
        )}
      </div>

      {isSelected && (
        <div className="sticky bottom-0 left-0 right-0 bg-white shadow-lg rounded-t-xl p-4 border-t-4 border-indigo-600 transition-all duration-300 transform">
          <div className="flex justify-between items-center mb-2">
            <div>
              <p className="text-sm text-gray-500 mb-1">Selected Items</p>
              <p className="text-xl font-bold text-indigo-800">{totalQuantity} Items</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-1">Total Amount</p>
              <p className="text-xl font-bold text-indigo-800">₹{totalAmount.toFixed(2)}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              className="py-3 px-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
              onClick={isAddingItems && existingHoldBillId ? handleProceed : handleDirectHold}
            >
              <Clock className="mr-2" size={18} />
              {isAddingItems && existingHoldBillId ? "Update Hold Bill" : "Hold Bill"}
            </button>
            <button
              className="py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors duration-200 flex items-center justify-center"
              onClick={isAddingItems && existingHoldBillId ? handleProceed : handleAddDetails}
            >
              <ShoppingBag className="mr-2" size={18} />
              {isAddingItems && existingHoldBillId ? "Update" : "Proceed"}
            </button>
          </div>
        </div>
      )}

      <div className="fixed top-20 right-4 z-50">
        {toasts.map((toast) => (
          <Toast key={toast.id} message={toast.message} type={toast.type} />
        ))}
      </div>

      <VariantSelectionModal />
      <HoldBillsSelectionModal />
      <CustomAlertModal />
    </div>
  )
}

export default MenuSelection
